import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { enumSubscriptionType } from '@app/data-layer/enums/common';
import { enumCompanyPaymentType } from '@app/data-layer/enums/companies';
import { enumOrganizationRole } from '@app/data-layer/enums/organizations';
import { enumPaymentProvider, enumPaymentState } from '@app/data-layer/enums/payments';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { PaginatedResultModel, SubscriptionModel } from '@app/data-layer/models/common';
import { CompanyModel } from '@app/data-layer/models/companies';
import { OrganizationMemberInvitationModel } from '@app/data-layer/models/organizations';
import { OrganizationMemberModel } from '@app/data-layer/models/organizations/organization-member-model';
import { OrganizationModel } from '@app/data-layer/models/organizations/organization-model';
import { BillModel, PaymentMethodModel } from '@app/data-layer/models/payments';
import { UserBetaFlagModel, UserModel } from '@app/data-layer/models/user';
import { BillsRepository, PaymentsRepository } from '@app/data-layer/repositories';
import { OrganizationsRepository } from '@app/data-layer/repositories/organizations.repository';
import { SubscriptionsRepository } from '@app/data-layer/repositories/subscriptions.repository';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { IHeaderItem } from '@app/modules/app-layout/interfaces';
import { CompaniesService } from '@services/companies.service';
import { OrganizationsService } from '@services/organizations.service';
import { Observable, ReplaySubject, forkJoin, of } from 'rxjs';
import { takeUntil, tap, take, switchMap, map, finalize } from 'rxjs/operators';

@Component({
  selector: 'account-settings-organization-page',
  templateUrl: './account-settings-organization-page.component.html',
  styleUrls: ['./account-settings-organization-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSettingsOrganizationPageComponent implements OnInit, OnDestroy {
  public organizationMembers: OrganizationMemberModel[];
  public organizationInvitedMembers: OrganizationMemberInvitationModel[];
  public organization: OrganizationModel;
  public organizationSubscriptions: SubscriptionModel[];
  public selectedSubscription: SubscriptionModel;
  public currentUser: UserModel;
  public currentUserMember: OrganizationMemberModel;
  public isPayPalPaymentTokenLoading: boolean;
  public isLoadingPaymentMethods: boolean;
  public allPaymentMethods: PaymentMethodModel[] = [];
  public paymentMethods: PaymentMethodModel[] = [];
  public monthlyBillList: BillModel[] = [];

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private organizationsRepository: OrganizationsRepository,
    private organizationsService: OrganizationsService,
    private companiesService: CompaniesService,
    private appLayoutService: AppLayoutService,
    private billsRepository: BillsRepository,
    private subscriptionsRepository: SubscriptionsRepository,
    private paymentsRepository: PaymentsRepository,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.companiesService.getUser();
    const firstCompany = this.companiesService.getCompanies()[0];

    this.organizationsService
      .getOrganizationByIdFromServer(this.currentUser.defaultOrganizationId || firstCompany.organizationId)
      .pipe(
        tap((organization) => {
          this.organization = organization;
          this.organizationsService.setDefaultOrganization(organization);
        }),
        switchMap((organization) => {
          return forkJoin([
            this.organizationsRepository.getOrganizationMembers(organization.id),
            this.organizationsRepository.getOrganizationInvitedMembers(organization.id),
          ]);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(([organizationMembers, organizationInvitedMembers]) => {
        this.currentUserMember = organizationMembers?.find((member) => member.user.userId === this.currentUser.uid);
        this.organizationMembers = organizationMembers;
        this.organizationInvitedMembers = organizationInvitedMembers;

        this.loadPaymentMethods();
        this.loadMonthlyBills();

        this.updateDashboardHeader();
        this.cdr.markForCheck();
      });

    this.subscriptionsRepository
      .getSubscriptions()
      .pipe(
        map((subscriptions) => {
          const basicPlan = {
            id: -1,
            type: enumSubscriptionType.BASIC,
            price: 0,
            priceForItem: 0,
            itemsCount: 0,
          };
          subscriptions.unshift(basicPlan);
          return subscriptions;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((subscriptions) => {
        this.organizationSubscriptions = subscriptions;
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onDeleteOrganizationMember(userId: number) {
    this.organizationsRepository
      .deleteOrganizationMember(this.organization.id, userId)
      .pipe(
        switchMap((_) => this.organizationsRepository.getOrganizationMembers(this.organization.id)),
        takeUntil(this.destroy$),
      )
      .subscribe((organizationMembers) => {
        this.organizationMembers = organizationMembers;
        this.cdr.markForCheck();
      });
  }

  public onDeleteOrganizationMemberInvite(invitationId: number) {
    this.organizationsRepository
      .deleteOrganizationMemberInvitation(invitationId)
      .pipe(
        switchMap(() => {
          return this.organizationsRepository.getOrganizationInvitedMembers(this.organization.id);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((organizationInvitedMembers: OrganizationMemberInvitationModel[]) => {
        this.organizationInvitedMembers = organizationInvitedMembers;
        this.cdr.markForCheck();
      });
  }

  public onInviteOrganizationMember(organizationInvitedMembers: OrganizationMemberInvitationModel[]) {
    this.organizationInvitedMembers = organizationInvitedMembers;
    this.cdr.markForCheck();
  }

  public onResendOrganizationMemberInvite(invitationId: number) {
    this.organizationsRepository
      .resendOrganizationMemberInvite(invitationId)
      .pipe(
        switchMap(() => {
          return this.organizationsRepository.getOrganizationInvitedMembers(this.organization.id);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((organizationInvitedMembers: OrganizationMemberInvitationModel[]) => {
        this.organizationInvitedMembers = organizationInvitedMembers;
        this.cdr.markForCheck();
      });
  }

  public onUpdateOrganizationMember(organizationMembers: OrganizationMemberModel[]) {
    this.organizationMembers = organizationMembers;
    this.cdr.markForCheck();
  }

  public onPaypalPaymentToken(): void {
    const params = this.route.snapshot.queryParams;
    const isApprovedReturnUrl = params.isApproved;
    const isApprovalTokenId = params.approval_token_id;
    const subscriptionId = params.subscriptionId;
    if (!!isApprovedReturnUrl && !!isApprovalTokenId) {
      this.isPayPalPaymentTokenLoading = true;

      this.paypalPaymentToken(this.organization.id, isApprovalTokenId)
        .pipe(
          switchMap(() => this.getOrganization(this.organization.id)),
          takeUntil(this.destroy$),
        )
        .subscribe((organization) => {
          this.removeQueryParams(['subscriptionId', 'isApproved', 'approval_token_id', 'approval_session_id']);
          this.organization = organization;
          this.organizationsService.setDefaultOrganization(this.organization);
          this.isPayPalPaymentTokenLoading = false;
          if (!!subscriptionId) {
            this.onUpdateSubscription(Number(subscriptionId));
          }
          this.cdr.markForCheck();
        });
    }
  }

  public onStripePaymentToken(): void {
    const params = this.route.snapshot.queryParams;
    const subscriptionId = params.subscriptionId;
    const redirectStatus = params.redirect_status;

    if (!!subscriptionId && !!redirectStatus && redirectStatus === 'succeeded') {
      this.onUpdateSubscription(Number(subscriptionId));
    }
    this.removeQueryParams(['subscriptionId', 'setup_intent', 'setup_intent_client_secret', 'redirect_status']);
  }

  public onRemoveMonthlyPayment(currentPaymentMethodProvider: enumPaymentProvider): void {
    let deletePaymentProviderMethod: Observable<CompanyModel | void>;
    switch (currentPaymentMethodProvider) {
      case enumPaymentProvider.STRIPE:
        deletePaymentProviderMethod = this.organizationsService.deleteDefaultPaymentMethod(this.organization.id, true);
        break;
      case enumPaymentProvider.PAYPAL:
        deletePaymentProviderMethod = this.paypalPaymentRemove(this.organization.id);
        break;
      default:
        deletePaymentProviderMethod = of();
        break;
    }

    deletePaymentProviderMethod
      .pipe(
        switchMap(() => this.getOrganization(this.organization.id)),
        takeUntil(this.destroy$),
      )
      .subscribe((organization) => {
        this.organization = organization;
        this.organizationsService.setDefaultOrganization(this.organization);
        this.cdr.markForCheck();
      });
  }

  public onUpdatePaymentType(customsPaymentType: enumCompanyPaymentType) {
    this.organizationsRepository
      .updatePaymentType(this.organization.id, customsPaymentType, true)
      .pipe(
        switchMap(() => this.getOrganization(this.organization.id)),
        takeUntil(this.destroy$),
      )
      .subscribe((organization) => {
        this.organization = organization;
        this.organizationsService.setDefaultOrganization(organization);
        this.cdr.markForCheck();
      });
  }

  public onUpdateSubscription(subscriptionId: number) {
    if (!this.organization?.defaultPaymentMethod?.id) {
      this.selectedSubscription = this.getSubscriptionById(subscriptionId);
      this.cdr.markForCheck();
    } else {
      this.organizationsRepository
        .updateSubscription(this.organization.id, subscriptionId, true)
        .pipe(
          switchMap(() => this.organizationsService.getOrganizationAsync(true)),
          take(1),
          takeUntil(this.destroy$),
        )
        .subscribe((organization) => {
          this.organization = organization;

          this.loadPaymentMethods();
          this.loadMonthlyBills();

          this.cdr.markForCheck();
        });
    }
  }

  public onRemoveSubscription() {
    this.organizationsRepository
      .deleteSubscription(this.organization.id, true)
      .pipe(
        switchMap(() => this.organizationsService.getOrganizationAsync(true)),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((organization) => {
        this.organization = organization;
        this.selectedSubscription = null;
        this.cdr.markForCheck();
      });
  }

  public onResetSelectedSubscription() {
    this.selectedSubscription = null;
    this.cdr.markForCheck();
  }

  public loadMonthlyBills(): void {
    this.loadMonthlyBillsCall()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: PaginatedResultModel<BillModel>) => {
        this.monthlyBillList = (result?.items || []).filter(
          (item: BillModel) => [enumPaymentState.DELAYED].includes(item.state) && !item.company,
        );
        this.cdr.markForCheck();
      });
  }

  public loadMonthlyBillsCall(): Observable<PaginatedResultModel<BillModel>> {
    if (!this.organization?.id) {
      return of(null);
    }
    return this.billsRepository.searchAll(
      {
        organizationId: this.organization.id,
        type: enumCompanyPaymentType.MONTHLY,
      },
      true,
    );
  }

  public onPayMonthlyBill(billId: number): void {
    switch (this.currentOrganizationPaymentMethod?.provider) {
      case enumPaymentProvider.STRIPE:
        this.organizationsService
          .organizationPayBill(billId)
          .pipe(takeUntil(this.destroy$))
          .subscribe((result: PaginatedResultModel<BillModel>) => {
            this.monthlyBillList = (result?.items || []).filter(
              (item: BillModel) => [enumPaymentState.DELAYED].includes(item.state) && !item.company,
            );
            this.cdr.markForCheck();
          });
        break;
      case enumPaymentProvider.PAYPAL:
        this.paymentsRepository
          .create(billId, true)
          .pipe(
            switchMap(() => this.loadMonthlyBillsCall()),
            takeUntil(this.destroy$),
          )
          .subscribe((result) => {
            this.monthlyBillList = (result?.items || []).filter(
              (item: BillModel) => [enumPaymentState.DELAYED].includes(item.state) && !item.company,
            );
            this.cdr.markForCheck();
          });
        break;
      default:
        break;
    }
  }

  public get isOrganizationAdmin(): boolean {
    return this.currentUserMember?.role === enumOrganizationRole.ADMIN;
  }

  public get isOrganizationMember(): boolean {
    return this.currentUserMember?.role === enumOrganizationRole.CONTRIBUTER;
  }

  private loadPaymentMethods(): void {
    this.isLoadingPaymentMethods = true;

    this.billsRepository
      .paymentMethods(this.companiesService.getCompany()?.id)
      .pipe(
        finalize(() => (this.isLoadingPaymentMethods = false)),
        takeUntil(this.destroy$),
      )
      .subscribe((paymentMethods: PaymentMethodModel[]) => {
        const skipMethods: number[] = [5 /*alipay*/];
        this.paymentMethods = paymentMethods.filter(
          (item: PaymentMethodModel) =>
            (item.provider === enumPaymentProvider.STRIPE ||
              (this.isHasPayPalFastCheckout && item.provider === enumPaymentProvider.PAYPAL)) &&
            !skipMethods.includes(item.codeId),
        ); // atm only stripe & paypal
        this.allPaymentMethods = paymentMethods;
        this.cdr.markForCheck();
      });
  }

  private get currentOrganizationPaymentMethod(): PaymentMethodModel {
    return (this.paymentMethods || []).find((item: PaymentMethodModel) => item.id === this.organization.defaultPaymentMethod.id);
  }

  private get isHasPayPalFastCheckout(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.paypalFastCheckout);
  }

  private getSubscriptionById(subscriptionId): SubscriptionModel {
    return this.organizationSubscriptions.find((subscription) => subscription.id === subscriptionId);
  }

  private getOrganization(organizationId: number): Observable<OrganizationModel> {
    return this.organizationsService.getOrganizationByIdFromServer(organizationId);
  }

  private paypalPaymentRemove(organizationId: number): Observable<void> {
    return this.billsRepository.paypalOrganizationPaymentRemove(organizationId);
  }

  private paypalPaymentToken(organizationId: number, approvalSessionId: string): Observable<void> {
    return this.billsRepository.paypalOrganizationPaymentToken(organizationId, approvalSessionId);
  }

  private removeQueryParams(paramsToRemove: string[]) {
    const queryParams = { ...this.route.snapshot.queryParams };

    paramsToRemove.forEach((param) => {
      delete queryParams[param];
    });

    this.router.navigate([], { queryParams });
  }

  private updateDashboardHeader(): void {
    const title: IHeaderItem[] = [];
    title.push(
      {
        key: 'MENU.ACCOUNT_SETTINGS',
        link: '/companies/account-settings',
      },
      {
        key: 'LABELS.ORGANIZATION',
        link: null,
      },
    );
    this.appLayoutService.contentHeader$.next({ title, mobileTitle: title });
  }
}
